const idrFmt = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 });
const idr2Fmt = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function idr(v: string | number | null | undefined, opts: { decimals?: boolean } = {}): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? Number(v) : v;
  if (!Number.isFinite(n)) return '—';
  return `Rp ${(opts.decimals ? idr2Fmt : idrFmt).format(n)}`;
}

/** Compact rupiah for headline figures: 297,4 jt / 1,2 M. */
export function idrCompact(v: string | number | null | undefined): { value: string; unit: string } {
  const n = Number(v ?? 0);
  if (Math.abs(n) >= 1e9) return { value: (n / 1e9).toLocaleString('id-ID', { maximumFractionDigits: 2 }), unit: 'M' };
  if (Math.abs(n) >= 1e6) return { value: (n / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 1 }), unit: 'jt' };
  if (Math.abs(n) >= 1e3) return { value: (n / 1e3).toLocaleString('id-ID', { maximumFractionDigits: 0 }), unit: 'rb' };
  return { value: n.toLocaleString('id-ID'), unit: '' };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function date(iso: string | null | undefined, opts: { year?: boolean; weekday?: boolean } = {}): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '—';
  const wd = opts.weekday ? `${DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ` : '';
  return `${wd}${d} ${MONTHS[m - 1]}${opts.year === false ? '' : ` ${y}`}`;
}

export function dateRange(from: string, to: string): string {
  if (from.slice(0, 7) === to.slice(0, 7)) return `${Number(from.slice(8, 10))}–${date(to)}`;
  if (from.slice(0, 4) === to.slice(0, 4)) return `${date(from, { year: false })} – ${date(to)}`;
  return `${date(from)} – ${date(to)}`;
}

export function time(ts: string | number | Date | null | undefined, timeZone?: string): string {
  if (!ts) return '—';
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone }).format(new Date(ts));
}

export function dayOf(ts: string | number | Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ts));
}

export function minutes(n: number): string {
  if (!n) return '0m';
  const h = Math.floor(n / 60);
  const m = n % 60;
  return h ? `${h}h${m ? ` ${String(m).padStart(2, '0')}m` : ''}` : `${m}m`;
}

export function relative(ts: string | number | Date): string {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 45) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 7 * 86_400) return `${Math.round(diff / 86_400)}d ago`;
  return date(new Date(ts).toISOString());
}

export function initials(name: string): string {
  const parts = name.replace(/^(I|Ni|Ni Luh|I Gede)\s+/i, '').split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase();
}

/** Stable warm hue per person (kept within ochre → clay → sage → dusk). */
export function hueFor(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const palette = [18, 28, 38, 46, 92, 140, 168, 205, 222, 330];
  return palette[h % palette.length]!;
}

export function greeting(timeZone?: string): string {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone }).format(new Date()));
  if (h < 11) return 'Selamat pagi';
  if (h < 15) return 'Selamat siang';
  if (h < 19) return 'Selamat sore';
  return 'Selamat malam';
}

export function todayIn(timeZone?: string): string {
  return dayOf(Date.now(), timeZone);
}

export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
